import React, { useState } from 'react';
import { Alert, Button, Empty, Spin, Table, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import CourseFormModal from './CourseFormModal';
import CourseDrawer from './CourseDrawer';
import { COURSE_STATUS_LABELS, canAuthorTraining, formatDdMmYyyy, languageLabel } from '../../utils/trainingModel';

const { Text } = Typography;
const STATUS_COLOURS = { draft: 'default', published: 'green', retired: 'red' };

/**
 * Course list + authoring entry for Advisor / Lead Advisor (training.controller.ts
 * `GET .../training/courses` returns every status to these personas).
 * The list itself is owned by TrainingPage (shared with the history tab's course-title join);
 * this component renders it and opens the detail drawer.
 */
const CourseManager = ({ projectId, role, members, courses, onReload }) => {
    const canAuthor = canAuthorTraining(role);
    const [createOpen, setCreateOpen] = useState(false);
    const [openCourseId, setOpenCourseId] = useState(null);

    const columns = [
        {
            title: 'Course',
            dataIndex: 'title',
            key: 'title',
            render: (title, c) => (
                <div>
                    <Text strong>{title}</Text>
                    <div>{(c.controlRefs || []).map((r) => <Tag key={r} color="blue">{r}</Tag>)}</div>
                </div>
            ),
        },
        { title: 'Version', dataIndex: 'version', key: 'version', width: 90, render: (v) => `v${v}` },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 120,
            render: (s) => <Tag color={STATUS_COLOURS[s] || 'default'}>{COURSE_STATUS_LABELS[s] || s}</Tag>,
        },
        { title: 'Modules', dataIndex: 'moduleCount', key: 'moduleCount', width: 90 },
        {
            title: 'Languages approved',
            key: 'languages',
            width: 180,
            render: (_, c) =>
                c.status === 'published' ? (c.selectableLanguages || ['en']).map(languageLabel).join(', ') : '-',
        },
        { title: 'Published', dataIndex: 'publishedAt', key: 'publishedAt', width: 120, render: (d) => formatDdMmYyyy(d) },
        {
            title: '',
            key: 'actions',
            width: 100,
            render: (_, c) => <Button size="small" onClick={() => setOpenCourseId(c.id)}>Open</Button>,
        },
    ];

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Text type="secondary">Courses for this project. A course is delivered to learners only once the Lead Advisor publishes it.</Text>
                {canAuthor && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>New course</Button>
                )}
            </div>

            {courses.status === 'loading' && !courses.data && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {courses.status === 'error' && (
                <Alert
                    type="error"
                    showIcon
                    message="Could not load training courses"
                    description={courses.error?.message}
                    action={<Button size="small" onClick={onReload}>Retry</Button>}
                />
            )}
            {courses.status === 'ready' && (courses.data || []).length === 0 && (
                <Empty description="No training courses yet.">
                    {canAuthor && <Button type="primary" onClick={() => setCreateOpen(true)}>Create the first course</Button>}
                </Empty>
            )}
            {(courses.data || []).length > 0 && courses.status !== 'error' && (
                <Table rowKey={(r) => r.id} columns={columns} dataSource={courses.data} pagination={false} loading={courses.status === 'loading'} />
            )}

            {canAuthor && (
                <CourseFormModal
                    open={createOpen}
                    projectId={projectId}
                    course={null}
                    onClose={() => setCreateOpen(false)}
                    onSaved={(course) => {
                        setCreateOpen(false);
                        onReload();
                        setOpenCourseId(course.id);
                    }}
                />
            )}
            <CourseDrawer
                open={!!openCourseId}
                projectId={projectId}
                courseId={openCourseId}
                role={role}
                members={members}
                onClose={() => setOpenCourseId(null)}
                onChanged={onReload}
                onOpenCourse={setOpenCourseId}
            />
        </div>
    );
};

export default CourseManager;
